/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useEffect, useState} from 'react'
import changelogData from './changelogs.json'

// Define the interface for each changelog entry, including a category.
interface ChangelogEntry {
  date: string
  title: string
  description: string
  category: 'feature' | 'bugfix' | 'qol'
}

// Helper function to compute how many days ago the entry was updated.
const getDaysAgo = (dateString: string): string => {
  const entryDate = new Date(dateString)
  const today = new Date()
  // Reset both dates to midnight for an accurate day count.
  entryDate.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)
  const diffTime = today.getTime() - entryDate.getTime()
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return '1 day ago'
  return `${diffDays} days ago`
}

// Returns an icon (emoji) based on the entry category.
const getCategoryIcon = (category: ChangelogEntry['category']): string => {
  switch (category) {
    case 'feature':
      return '✨'
    case 'bugfix':
      return '🐞'
    case 'qol':
      return '⚙️'
    default:
      return ''
  }
}

// Returns additional styling based on the entry category.
const getCategoryStyles = (category: ChangelogEntry['category']): React.CSSProperties => {
  switch (category) {
    case 'feature':
      return {borderLeft: '5px solid #34c759'} // Green for feature updates
    case 'bugfix':
      return {borderLeft: '5px solid #ff3b30'} // Red for bug fixes
    case 'qol':
      return {borderLeft: '5px solid #5856d6'} // Purple for QOL updates
    default:
      return {}
  }
}

// Helper function to render the description. If it includes bullet markers (lines starting with '-'),
// it splits the description into an unordered list; otherwise, it renders a simple paragraph.
const renderDescription = (description: string) => {
  // Check if the description contains bullet markers (e.g., starts with '-' or has '\n-')
  if (description.includes('\n-') || description.trim().startsWith('-')) {
    const lines = description.split('\n').filter((line) => line.trim() !== '')
    return (
      <ul style={styles.bulletList}>
        {lines.map((line, index) => {
          let content = line.trim()
          if (content.startsWith('-')) {
            content = content.slice(1).trim()
          }
          return (
            <li key={index} style={styles.bulletItem}>
              {content}
            </li>
          )
        })}
      </ul>
    )
  }
  // If no bullet markers, render as a normal paragraph.
  return <p style={styles.entryDescription}>{description}</p>
}

export const ChangelogPage: FC = () => {
  const [changelogs, setChangelogs] = useState<ChangelogEntry[]>([])

  useEffect(() => {
    // Flatten in case the JSON has nested arrays (or simply use it directly if it’s flat)
    const flattenedLogs = Array.isArray(changelogData)
      ? (changelogData.flat() as ChangelogEntry[])
      : []
    setChangelogs(flattenedLogs)
  }, [])

  // Sort entries by date descending so the newest logs appear first.
  const sortedLogs = changelogs.slice().sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime()
  })

  return (
    <div style={styles.changelogContainer}>
      {sortedLogs.map((entry, index) => (
        <div
          key={index}
          style={{
            ...styles.entryCard,
            ...getCategoryStyles(entry.category),
          }}
        >
          <div style={styles.dateText}>
            {entry.date} <span style={styles.daysAgo}>({getDaysAgo(entry.date)})</span>
          </div>
          <h2 style={styles.entryTitle}>
            <span style={styles.icon}>{getCategoryIcon(entry.category)}</span>
            {entry.title}
          </h2>
          {renderDescription(entry.description)}
        </div>
      ))}
    </div>
  )
}

// A simple legend component to explain the icons.
const Legend: FC = () => {
  return (
    <div style={styles.legendContainer}>
      <div style={styles.legendItem}>
        <span style={styles.legendIcon}>✨</span> Feature Update
      </div>
      <div style={styles.legendItem}>
        <span style={styles.legendIcon}>🐞</span> Bug Fix
      </div>
      <div style={styles.legendItem}>
        <span style={styles.legendIcon}>⚙️</span> QOL Update
      </div>
    </div>
  )
}

export const ChangelogWrapper: FC = () => {
  return (
    <div style={styles.pageWrapper}>
      <h1 style={styles.pageHeader}>Changelog</h1>
      <Legend />
      <ChangelogPage />
    </div>
  )
}

const styles: {[key: string]: React.CSSProperties} = {
  pageWrapper: {
    color: '#f2f2f7',
    minHeight: '100vh',
    padding: '30px',
    fontFamily: 'San Francisco, -apple-system, BlinkMacSystemFont, sans-serif',
  },
  pageHeader: {
    marginBottom: '20px',
    fontSize: '2rem',
    fontWeight: 600,
  },
  legendContainer: {
    display: 'flex',
    gap: '20px',
    marginBottom: '20px',
    fontSize: '0.9rem',
    color: '#8e8e93',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
  },
  legendIcon: {
    marginRight: '8px',
    fontSize: '1.2rem',
  },
  changelogContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  entryCard: {
    backgroundColor: '#111125',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.25)',
  },
  dateText: {
    fontSize: '0.9rem',
    color: '#8e8e93',
    marginBottom: '8px',
  },
  daysAgo: {
    marginLeft: '8px',
    fontSize: '0.9rem',
    color: '#8e8e93',
  },
  entryTitle: {
    margin: '0 0 12px 0',
    fontSize: '1.5rem',
    lineHeight: 1.2,
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginRight: '10px',
    fontSize: '1.5rem',
  },
  entryDescription: {
    fontSize: '1rem',
    lineHeight: 1.6,
    margin: 0,
  },
  bulletList: {
    paddingLeft: '20px',
    margin: 0,
  },
  bulletItem: {
    marginBottom: '8px',
    fontSize: '1rem',
    lineHeight: 1.6,
  },
}
